import { useTheme } from "@mui/material";
import { Link } from "react-router-dom";
import {
  street,
  city,
  state,
  zip,
  contactPath,
} from "../../constants";
import { ADDRESS_MAPS_URL } from "../../menu/menuShared";

const LAST_UPDATED = "August 27, 2026";

const PrivacyPolicy = () => {
  const theme = useTheme();

  const eyebrowStyle = {
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.palette.brand.brass,
    margin: "0 0 6px",
  };

  const headingStyle = {
    fontFamily: theme.typography.secondaryFontFamily,
    fontWeight: 600,
    color: theme.palette.primary.dark,
    fontSize: "min(9vw, 40px)",
    margin: "0 0 12px",
  };

  const sectionHeadingStyle = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: 700,
    fontSize: "17px",
    color: theme.palette.primary.dark,
    margin: "0 0 10px",
  };

  const bodyStyle = {
    fontFamily: theme.typography.paragraphFontFamily,
    fontSize: "15px",
    lineHeight: 1.7,
    color: theme.palette.text.primary,
    margin: "0 0 12px",
  };

  const sectionStyle = {
    padding: "24px 0",
    borderBottom: `1px solid ${theme.palette.brand.border}`,
  };

  const linkStyle = {
    color: theme.palette.primary.main,
    fontWeight: 600,
    textDecoration: "none",
  };

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.default,
        fontFamily: theme.typography.fontFamily,
        paddingTop: "var(--nav-height)",
      }}
    >
      <div style={{ maxWidth: "720px", margin: "0 auto", padding: "36px 24px 64px" }}>
        <div style={eyebrowStyle}>Legal</div>
        <h1 style={headingStyle}>Privacy Policy</h1>
        <p style={{ ...bodyStyle, color: theme.palette.text.secondary }}>
          This Privacy Policy explains how Bethel Bible Church ("Bethel," "we," "us") collects, uses,
          and protects information from visitors ("Users") of bethelbibleoc.com (the "Site"). It applies
          to the Site and to the ministries and services we offer through it.
        </p>

        <div style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Information you give us</h2>
          <p style={bodyStyle}>
            We collect personal information only when you choose to give it to us - for example, by
            submitting the contact form, signing up for something, or reaching out about a prayer
            request or question. This is typically limited to your name, email address, and whatever
            you write in your message. You're always welcome to browse the Site anonymously, and
            declining to share information only means we can't respond to a request that requires it.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Information collected automatically</h2>
          <p style={bodyStyle}>
            Like most websites, the Site automatically picks up some technical information whenever you
            visit - your browser type, general device type, and how you found or moved through the
            Site. We use Google Analytics (via Firebase) to understand this in aggregate, so we can tell
            whether the Site is actually useful to people, not to identify individual visitors. Your
            browser lets you block or clear these cookies at any time; doing so won't stop you from
            using the Site, though a few embedded features may not work as smoothly.
          </p>
          <p style={bodyStyle}>
            Content embedded directly from other services - YouTube videos on our Services page, the map
            on our Contact page - may set their own cookies under their own privacy policies, which we
            don't control.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>How we use it</h2>
          <ul style={{ ...bodyStyle, paddingLeft: "20px", margin: 0 }}>
            <li style={{ marginBottom: "8px" }}>
              <strong style={{ color: theme.palette.primary.dark }}>To respond to you</strong> - answering
              a message, question, or prayer request you've sent us.
            </li>
            <li style={{ marginBottom: "8px" }}>
              <strong style={{ color: theme.palette.primary.dark }}>To improve the Site</strong> - understanding,
              in aggregate, how people use it so we can make it more useful.
            </li>
            <li>
              <strong style={{ color: theme.palette.primary.dark }}>To keep you informed</strong> - if you've
              asked to hear from us about something specific, like an event or ministry update.
            </li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Online giving</h2>
          <p style={bodyStyle}>
            Donations and giving are handled entirely by Planning Center Giving through Church Center,
            not by this Site. When you give, you're submitting your payment information directly to
            Planning Center - Bethel never receives or stores your card or bank details. Planning
            Center's own privacy practices, available at{" "}
            <a href="https://www.planningcenter.com/privacy" target="_blank" rel="noreferrer" style={linkStyle}>
              planningcenter.com/privacy
            </a>
            , govern how that information is handled.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>How we protect your information</h2>
          <p style={bodyStyle}>
            We take reasonable steps to protect any information you share with us against unauthorized
            access, alteration, disclosure, or loss. No method of transmission or storage is perfectly
            secure, but we don't ask for or store sensitive information like payment details ourselves -
            that's handled by Planning Center, as described above.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Sharing your information</h2>
          <p style={bodyStyle}>
            We do not sell, rent, or trade your personal information. We share it only with the service
            providers that help us run the Site and our ministries - for example, the platform that
            delivers our contact form, or Planning Center for giving - and only so they can perform that
            specific service for us.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Third-party links</h2>
          <p style={bodyStyle}>
            The Site links out to services we don't control - Church Center, YouTube, our social media
            pages, and Google Maps among them. Once you leave our Site for one of these, that service's
            own privacy policy applies, not ours.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Children's privacy</h2>
          <p style={bodyStyle}>
            The Site itself isn't directed at children, and we don't knowingly collect personal
            information from children online through it. Information for our children's and youth
            ministries (Awana, camps, and similar programs) is collected in person through the ministry
            itself, not through this website.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Changes to this policy</h2>
          <p style={bodyStyle}>
            We may update this Privacy Policy from time to time. When we do, we'll update the date at
            the bottom of this page. We encourage you to check back occasionally - continuing to use the
            Site after a change means you accept the updated policy.
          </p>
        </div>

        <div style={{ padding: "24px 0" }}>
          <h2 style={sectionHeadingStyle}>Contacting us</h2>
          <p style={bodyStyle}>
            Questions about this policy or how we handle your information? Reach us through our{" "}
            <Link to={contactPath} style={linkStyle}>
              Contact page
            </Link>
            , or in person:
          </p>
          <p style={{ ...bodyStyle, marginBottom: "4px" }}>
            <strong>Bethel Bible Church</strong>
          </p>
          <p style={bodyStyle}>
            <a href={ADDRESS_MAPS_URL} target="_blank" rel="noreferrer" style={linkStyle}>
              {street}, {city}, {state} {zip}
            </a>
          </p>
        </div>

        <p style={{ fontFamily: theme.typography.fontFamily, fontSize: "12px", color: theme.palette.text.secondary }}>
          Last updated {LAST_UPDATED}.
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
